/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import * as PapaParse from 'papaparse';
import { v4 as uuidv4 } from 'uuid';

// Component imports
import { Button, Col, Divider, Form, Input, Popconfirm, Row, Space, Table, Typography, Upload, Tooltip, Select } from 'antd';
import { CloseCircleFilled, PlusOutlined, SearchOutlined, ExportOutlined, EditOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import ActivityLink from '../UI/ActivityLink';

// Misc imports
import replaceUrlParams from '../../utils/string/replaceUrlParams';
import { useBotLanguage } from '../../utils/hooks/useBotLanguage';
import { NAV_BOT } from '../../constants/navigation';
import { useModalConfirm } from '../../utils/hooks/useModalConfirm';
import { truncate } from '../../utils/string/truncate';
import { DEFAULT_FALLBACK_ID, NODE_TYPES } from '../../constants/flowGraph';
import openConsoleAlertMessage from '../../utils/misc/openConsoleAlertMessage';

// Constant declarations
const CSV_PARSER_OPTIONS = {
  header: true,
  dynamicTyping: true,
  skipEmptyLines: true,
  transformHeader: (header) => header?.toLowerCase().replace(/\W/g, '_'),
  transform: (value) => value
};

/**
 * Table to view and manage NLP keywords for an automated answer
 * @param {Array} automatedAnswers List of automated answers { items: [aa1, aa2, ...], total: 0 }
 * @param {Object} intents AA ID to keywords map
 * @param {Function} onSave Function to save the keywords
 * @param {Function} onModalDismiss Function to dismiss the modal
 * @param {String} selectedAA The selected automated answer ID
 * @param {Function} onCancel Function to cancel the operation
 * @param {Boolean} isUpdating Whether the keywords are being updated
 * @param {Boolean} isSideModalClosing Whether the side modal is closing
 * @param {String} bodyClassName The class name for the body of the modal
 * @param {String} footerClassName The class name for the footer of the modal
 */
const AddNlpKeywordsTable = ({
  automatedAnswers = [],
  intents = {},
  onSave = () => {},
  onModalDismiss = () => {},
  selectedAA = null,
  onCancel = () => {},
  isUpdating = false,
  isSideModalClosing = false,
  bodyClassName = '',
  footerClassName = ''
}) => {
  const [form] = Form.useForm();
  const { currentBotLanguage } = useBotLanguage();
  const { t } = useTranslation();
  const { tenantId, orgId } = useParams();

  const [searchFilter, setSearchFilter] = useState('');
  const [keywords, setKeywords] = useState([]); //each keyword is stored as an object: { id: string, text: string }
  const [isAdding, setIsAdding] = useState(false);
  const [sortedInfo, setSortedInfo] = useState({ order: '', columnKey: '' });
  const [localSelectedAA, setLocalSelectedAA] = useState(undefined);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editingValue, setEditingValue] = useState('');

  const { showModalConfirm } = useModalConfirm(hasPendingChanges);

  const editInputRef = useRef(null);

  useEffect(() => {
    if (editingId && editInputRef.current) {
      editInputRef.current.focus();
    }
  }, [editingId]);

  const initSelectedAA = (selectedAA) => {
    if (!selectedAA) {
      return undefined;
    }
    const matchedAnswer = automatedAnswers?.items?.find(answer => answer?.id === selectedAA) || null;
    if (matchedAnswer) {
      return {
        value: matchedAnswer.name[currentBotLanguage],
        key: matchedAnswer.id
      };
    }
    return {
      // Cases where an AA is deleted from bot but exists in intents, must display as selected
      value: selectedAA,
      key: selectedAA,
      deleted: true
    };
  };

  useEffect(() => {
    setLocalSelectedAA(initSelectedAA(selectedAA));
  }, [selectedAA]);

  useEffect(() => {
    if (localSelectedAA?.key) {
      const matchedIntent = Object.entries(intents).find(([intentId]) => {
        return localSelectedAA.key === intentId;
      })?.[1];

      if (matchedIntent) {
        const mapped = matchedIntent.map((keyword) => ({
          id: uuidv4(),
          text: keyword
        }));
        setKeywords(mapped);
      } else {
        setKeywords([]);
      }
    }
  }, [localSelectedAA, intents]);

  useEffect(() => {
    if (isAdding) {
      form.getFieldInstance('addKeyword')?.focus();
    }
  }, [isAdding]);

  useEffect(() => {
    if (isSideModalClosing) {
      handleShowConfirm(onCancel);
    }
  }, [isSideModalClosing]);

  const handleTableChange = (_pagination, _filters, sorter) => {
    setSortedInfo(sorter);
  };

  const handleAASelect = (value) => {
    setLocalSelectedAA(value);
    form.resetFields();
  };

  const saveKeywords = (action) => {
    const stringKeywords = keywords.map((keyword) => keyword.text); // keep only keywords text

    const valuesToSubmit = {
      intents: {
        [localSelectedAA.key]: stringKeywords
      }
    };
    onSave(valuesToSubmit, action);
  };

  const validateKeyword = (newKeyword, excludeId = null) => {
    if (!newKeyword || newKeyword.trim() === '') {
      return t('Please add a keyword');
    }
    // Check duplicates within current AA, excluding the keyword being edited
    const currentListExists = keywords.some(
      (keyword) => keyword.id !== excludeId && keyword.text === newKeyword
    );

    if (currentListExists) {
      return t('This keyword already exists');
    }

    // Check if the new keyword already exists in other AAs
    const otherKeywords = Object.entries(intents)
      .filter(([intentId]) => intentId !== localSelectedAA.key) // Exclude current AA
      .flatMap(([, kws]) => kws);

    if (otherKeywords.includes(newKeyword)) {
      // Find the AA that contains the keyword
      const automatedAnswerId = Object.keys(intents).find((intentId) =>
        intents[intentId].includes(newKeyword)
      );
      const automatedAnswer = automatedAnswers?.items.find(
        (answer) => answer.id === automatedAnswerId
      );
      const introNodeId = automatedAnswer?.content.nodes.find(
        (node) => node.type === NODE_TYPES.INTRO
      )?.id;

      // If there's an intro node id, show an ActivityLink. Otherwise, show plain text
      return introNodeId ? (
        <Space
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          {t('This keyword already exists:')}
          <ActivityLink entryPointId={introNodeId} openAAInNewTab />
        </Space>
      ) : t('This keyword already exists in another automated answer');
    }

    return null; // No errors
  };

  const handleAddKeyword = (values) => {
    const newKeyword = values['addKeyword'];
    const error = validateKeyword(newKeyword);
    if (error) {
      form.setFields([
        {
          name: 'addKeyword',
          errors: [error]
        }
      ]);
      return;
    }

    // Add the new keyword to the list with a fresh id
    const newKeywordObject = {
      id: uuidv4(),
      text: newKeyword
    };

    setKeywords((prev) => [newKeywordObject, ...prev]);
    setHasPendingChanges(true);
    form.resetFields();
  };

  const handleDelete = (keywordId) => {
    const updatedKeywords = keywords.filter((item) => item.id !== keywordId);
    setKeywords(updatedKeywords);
    setHasPendingChanges(updatedKeywords.length > 0);
  };

  const handleDeleteAll = () => {
    setKeywords([]);
    setHasPendingChanges(false);
  };

  const handleUpload = (file, handler) => {
    const reader = new FileReader();
    reader.onload = async (event) => {
      const csvData = PapaParse.parse(
        event.target.result,
        Object.assign(CSV_PARSER_OPTIONS, {
          error: (error) => {
            console.error('Parsing error:', error);
          },
          encoding: 'UTF-8'
        })
      );

      // Convert data to our schema
      const importedKeywords = csvData?.data
        .filter(row => typeof row.keywords === 'string' && row.keywords.trim())
        .map(row => row.keywords.trim()); // Extract just the keyword string

      if (importedKeywords.length) {
        handler(importedKeywords);
      } else {
        openConsoleAlertMessage({ type: 'error', message: t('No keywords to process') });
      }
    };

    reader.readAsText(file);
    return false; // Prevent default upload behavior
  };

  const handleCsvImport = (importedKeywords) => {
    // Convert local keywords to a Set for quick lookup
    const existingKeywordsSet = new Set(keywords.map((keyword) => keyword.text));

    const otherKeywordsSet = new Set(Object.entries(intents)
      .filter(([intentId]) => intentId !== localSelectedAA.key) // Exclude current AA
      .flatMap(([, keywords]) => keywords));

    // Filter out the imported keywords that are already in the existing keywords set
    let sanitizedKeywords = importedKeywords
      .filter(
        (keyword) => !existingKeywordsSet.has(keyword) // First search through existingKeywordSet
      )
      .filter(
        (keyword) => !otherKeywordsSet.has(keyword) // then search through already saved keywords
      );

    // Keep the duplicate keywords that are already saved in order to show them to the user
    const duplicateKeywords = importedKeywords.filter(
      (keyword) => otherKeywordsSet.has(keyword) // Include only keywords in otherKeywordsSet
    );

    if (sanitizedKeywords.length === 0) {
      openConsoleAlertMessage({ type: 'warning', message: t('All keywords are already imported.') });
    } else {
      if (duplicateKeywords.length) {
        //Display a warning to the user that some keywords where not imported as they are already saved
        openConsoleAlertMessage({
          type: 'warning',
          duration: 10,
          message: t(`Keywords: "${[...duplicateKeywords]}" already exist and won't be imported from the csv file.`) });
      }

      // Convert to { id, text }
      const newKeywordObjects = sanitizedKeywords.map((keyword) => ({
        id: uuidv4(),
        text: keyword
      }));

      setKeywords((prevKeywords) => [...newKeywordObjects, ...prevKeywords]);
      setHasPendingChanges(true);
    }
  };

  const handleShowConfirm = (action, actionType) => {
    showModalConfirm({
      defaultAction: () => {
        action();
        setHasPendingChanges(false);
      },
      onConfirm: () => {
        saveKeywords(actionType);
        if (action) {
          action();
        }
        setHasPendingChanges(false);
      },
      onMaskClick: () => {
        onModalDismiss();
      },
      okText: t('Save Changes'),
      cancelText: actionType === 'select' ? t('Continue without saving') : t('Quit without saving'),
      title: t('Save Changes?'),
      content:
        actionType === 'select'
          ? t('Your unsaved changes will be lost. Save changes before continue?')
          : t('Your unsaved changes will be lost. Save changes before closing?')
    });
  };

  const toggleEdit = (keywordId) => {
    const item = keywords.find((keyword) => keyword.id === keywordId);

    if (!item) {
      return;
    }

    form.setFieldsValue({
      editKeyword: item.text
    });

    setEditingId(keywordId);
    setEditingValue(item.text);
    if (isAdding) {
      setIsAdding(false);
      form.resetFields(['addKeyword']);
    }
  };

  const handleSaveEdit = () => {
    if (editingValue.trim()) {
      const index = keywords.findIndex((keyword) => keyword.id === editingId);

      if (index > -1) {
        if (keywords[index].text !== editingValue) {
          const error = validateKeyword(editingValue, editingId);
          if (error) {
            form.setFields([
              {
                name: 'editKeyword',
                errors: [error]
              }
            ]);
            return;
          }

          // Only update if the value has actually changed.
          const updatedKeywords = [...keywords];
          updatedKeywords[index] = { ...updatedKeywords[index], text: editingValue };
          setKeywords(updatedKeywords);
          setHasPendingChanges(true);
        }
      }
    }

    setEditingId(null);
    setEditingValue('');
    form.resetFields(['editKeyword']);
  };

  const dataSource = useMemo(() => {
    const addKeywordRow = { key: '__add_row__', rowType: 'addRow' }; // "dummy" row to handle the Add Keyword UI
    return [
      addKeywordRow,
      ...keywords
        // if editingId is present, that means there is an error in the edit input and must remain visible (otherwise blur will handle it)
        .filter((keyword) => keyword.id === editingId || keyword.text?.toLowerCase().includes(searchFilter.toLowerCase()))
        .map((keyword) => ({
          key: keyword.id,
          keyword: keyword.text,
          rowType: 'keyword'
        }))
    ];
  }, [keywords, searchFilter, editingId]);

  const columns = [
    {
      title: t('Keywords'),
      dataIndex: 'keyword',
      key: 'keyword',
      sorter: (a, b) => {
        if (a.rowType === 'addRow' || b.rowType === 'addRow') {
          return 0;
        }
        return a.keyword?.localeCompare(b.keyword);
      },
      sortOrder: sortedInfo.columnKey === 'keyword' && sortedInfo.order,
      onCell: (record) => {
        if (record.rowType !== 'addRow') {
          return {
            onDoubleClick: () => toggleEdit(record.key),
            style: { cursor: 'pointer', userSelect: 'none' }
          };
        }
        return {};
      },
      render: (text, record) => {
        // Handle the special "Add Keyword" row
        if (record.rowType === 'addRow') {
          if (!isAdding) {
            return {
              children: (
                <Button
                  disabled={!localSelectedAA}
                  type="link"
                  icon={<PlusOutlined />}
                  onClick={() => setIsAdding(true)}
                  size="small"
                  className="p-0"
                >
                  {t('Add Keyword')}
                </Button>
              ),
              props: { colSpan: 2 } // Merge both columns
            };
          }
          // Inline form for adding a new keyword
          return {
            children: (
              <Row align="top" justify="space-between">
                <Col flex="1">
                  <Form.Item
                    name="addKeyword"
                    validateTrigger="onSubmit"
                    className="mb-0 mr-2"
                  >
                    <Input
                      autoFocus
                      placeholder={t('Enter a keyword')}
                      onChange={() => {
                        if (form.getFieldError('addKeyword').length > 0) {
                          // Reset submit error when typing
                          form.setFields([{ name: 'addKeyword', errors: [] }]);
                        }
                      }}
                      onPressEnter={(event) => {
                        form.submit();
                        event.preventDefault(); //prevents triggering empty error
                      }}
                    />
                  </Form.Item>
                </Col>

                <Col>
                  <Space>
                    <Button type="primary" onClick={form.submit}>
                      {t('Add')}
                    </Button>
                    <Button
                      onClick={() => {
                        setIsAdding(false);
                        form.resetFields(['addKeyword']);
                      }}
                    >
                      {t('Cancel')}
                    </Button>
                  </Space>
                </Col>
              </Row>
            ),
            props: { colSpan: 2 }
          };
        }

        // Normal row: if this is the editing row => show inline edit
        if (record.key === editingId) {
          return (
            <Form.Item name="editKeyword" className="mb-0 mr-2">
              <Input
                ref={editInputRef}
                value={editingValue}
                placeholder={t('Enter a keyword')}
                onChange={(e) => {
                  // Clear validation errors if user types again
                  if (form.getFieldError('editKeyword').length > 0) {
                    // Reset submit error when typing
                    form.setFields([{ name: 'editKeyword', errors: [] }]);
                  }
                  setEditingValue(e.target.value);
                }}
                onPressEnter={handleSaveEdit}
                onBlur={handleSaveEdit}
              />
            </Form.Item>
          );
        }

        // Normal read-only cell
        return (
          <Tooltip title={record.keyword.length > 70 ? record.keyword : ''}>
            <Highlighter
              highlightClassName="bg-highlight p-0"
              searchWords={[searchFilter]}
              autoEscape
              textToHighlight={truncate(record.keyword, 70, '...')}
            />
          </Tooltip>
        );
      }
    },
    {
      title: (
        <Popconfirm
          disabled={!localSelectedAA || keywords.length === 0}
          title={
            <Fragment>
              <Typography.Text className="fw-bold">
                {t('Delete Keywords?')}<br/>
              </Typography.Text>
              <Typography.Text>
                {t('All keywords will be deleted')}
              </Typography.Text>
            </Fragment>
          }
          okText={t('Delete')}
          cancelText={t('Cancel')}
          placement="topRight"
          onConfirm={handleDeleteAll}
        >
          <Button
            type="link"
            size="small"
            className="p-0"
            disabled={!localSelectedAA || keywords.length === 0}
          >
            {t('Delete All')}
          </Button>
        </Popconfirm>
      ),
      key: 'deleteAll',
      width: 90,
      onCell: () => ({ style: { verticalAlign: 'top' } }), // when error in edit input appears, keep the buttons aligned top
      render: (_text, record) => {
        // Hide second column for the "addRow"
        if (record.rowType === 'addRow') {
          return {
            children: null,
            props: { colSpan: 0 }
          };
        }

        return (
          <div className="text-center">
            <Button
              type="text"
              size="small"
              shape="circle"
              icon={<EditOutlined className="text-muted fs-sm mr-2" />}
              onClick={() => toggleEdit(record.key)}
            />
            <Button
              type="text"
              size="small"
              shape="circle"
              icon={<CloseCircleFilled className="text-muted fs-sm" />}
              onClick={() => handleDelete(record.key)}
            />
          </div>
        );
      }
    }
  ];

  return (
    <Fragment>
      <div className={`${bodyClassName} mb-6`}>
        <Col className="mb-1 px-6">
          <Typography.Text>
            {t('Select Automated Answer')}
          </Typography.Text>
        </Col>

        <Col className="mb-10 px-6">
          <Select
            className="w-70"
            size="large"
            showSearch
            filterOption={(inputValue, option) => option.searchValue.toLowerCase()?.includes(inputValue.toLowerCase())}
            onSelect={(label, option) => {
              handleShowConfirm(() => handleAASelect(option), 'select');
            }}
            placeholder={
              <Space size={4}>
                <SearchOutlined className="text-secondary" />
                {t('Search or select automated answer')}
              </Space>
            }
            value={
              localSelectedAA && !localSelectedAA.deleted
                ? {
                  value: localSelectedAA.value,
                  label: (
                    <Row align="middle" className="text-truncate">
                      <Typography.Text ellipsis style={{ maxWidth: 'calc(100% - 24px)' }}>
                        {localSelectedAA.value}
                      </Typography.Text>
                      <Typography.Link
                        className="p-0 w-auto word-break-break-all pointer-events-all ml-3"
                        onClick={() => {
                          window.open(`${replaceUrlParams(NAV_BOT, { orgId, tenantId })}?view=behavior&tab=automated-answers&edit=${localSelectedAA.key}`, '_blank ');
                        }}
                      >
                        <ExportOutlined />
                      </Typography.Link>
                    </Row>
                  )
                }
                : localSelectedAA && localSelectedAA.deleted
                  ? {
                    value: localSelectedAA.key,
                    label: localSelectedAA.key
                  }
                  : undefined
            }
            options={(automatedAnswers?.items || [])
              .filter(answer => answer.id !== DEFAULT_FALLBACK_ID) // Ignore default fallback
              .map(answer => ({
                label: answer.name[currentBotLanguage],
                key: answer.id,
                value: answer.name[currentBotLanguage],
                searchValue: answer.name[currentBotLanguage]
              }))
            }
          />
        </Col>

        <Divider />

        <Space size={16} align="center" className="mb-4 px-6" wrap>
          <Input
            size="large"
            placeholder={t('Search keyword')}
            allowClear
            prefix={<SearchOutlined className="text-muted" />}
            onChange={(e) => setSearchFilter(e.target.value)}
          />

          <Upload
            accept=".csv"
            className="ant-upload-w-100"
            showUploadList={false}
            beforeUpload={(file) => {
              handleUpload(file, (payload) => handleCsvImport(payload));
            }}
          >
            <Button
              disabled={!localSelectedAA}
              icon={<PlusOutlined />}
              className="d-block ml-auto"
              size="large"
            >
              {t('Import from CSV')}
            </Button>
          </Upload>
        </Space>

        <Form form={form} onFinish={handleAddKeyword}>
          <Table
            className="px-6"
            dataSource={dataSource}
            columns={columns}
            pagination={false}
            rowKey="key"
            onChange={handleTableChange}
          />
        </Form>
      </div>

      <div className={footerClassName}>
        <Row justify="end" gutter={8}>
          <Col>
            <Button
              size="large"
              onClick={() => handleShowConfirm(onCancel)}
              disabled={isUpdating}
            >
              {t('Cancel')}
            </Button>
          </Col>
          <Col>
            <Button
              disabled={!hasPendingChanges}
              loading={isUpdating}
              className="float-right"
              type="primary"
              size="large"
              onClick={saveKeywords}
            >
              {t('Save Changes')}
            </Button>
          </Col>
        </Row>
      </div>
    </Fragment>
  );
};

AddNlpKeywordsTable.propTypes = {
  automatedAnswers: PropTypes.array,
  intents: PropTypes.object,
  selectedAA: PropTypes.string,
  onSave: PropTypes.func,
  onModalDismiss: PropTypes.func,
  isUpdating: PropTypes.bool,
  isSideModalClosing: PropTypes.bool,
  onCancel: PropTypes.func,
  bodyClassName: PropTypes.string,
  footerClassName: PropTypes.string
};

export default AddNlpKeywordsTable;
